'use client';

import { type FC, useMemo } from 'react';

import { ProgressRootContext } from './context/progress-root.context';
import type { ProgressRootPropsInterface } from './types/progress-root.types';

/**
 * Composable wrapper for the ProgressBar component.
 *
 * @related ['ProgressBar']
 * @since 10.0.0
 */
export const ProgressRoot: FC<ProgressRootPropsInterface> = (props) => {
  const {
    'aria-describedby': ariaDescribedby,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    children,
    minValue = 0,
    maxValue = 100,
    size = 'sm',
    stepperOffset = false,
    value,
  } = props;

  const percentComplete = useMemo(() => {
    if (value >= maxValue) {
      return 100;
    }

    if (value > minValue && value < maxValue) {
      if (stepperOffset) {
        let offsetValue = value - 1;
        let offsetMaxValue = maxValue - 1;
        return ((offsetValue - minValue) / (offsetMaxValue - minValue)) * 100;
      }

      return ((value - minValue) / (maxValue - minValue)) * 100;
    }

    return 0;
  }, [value, minValue, maxValue, stepperOffset]);

  const contextValue = useMemo(
    () => ({
      'aria-describedby': ariaDescribedby,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      value,
      maxValue,
      minValue,
      size,
      percentComplete,
      stepperOffset,
    }),
    [
      ariaDescribedby,
      ariaLabel,
      ariaLabelledby,
      value,
      maxValue,
      minValue,
      size,
      percentComplete,
      stepperOffset,
    ],
  );

  return (
    <ProgressRootContext.Provider value={contextValue}>{children}</ProgressRootContext.Provider>
  );
};

ProgressRoot.displayName = 'Progress.Root';
